// Audio Manager - Sound effects and background music using Howler.js
import { Howl, Howler } from 'howler';

export class Audio {
    private static instance: Audio;

    private sounds: Map<string, Howl> = new Map();
    private sfxVolume: number = 0.5;
    private isMuted: boolean = false;

    private bgm: Howl | null = null;
    private bgmId: number | null = null;
    private isBgmMuted: boolean = false;

    private constructor() {
        this.preloadSounds();
    }

    static getInstance(): Audio {
        if (!Audio.instance) {
            Audio.instance = new Audio();
        }
        return Audio.instance;
    }

    private preloadSounds(): void {
        // Define sounds (using web audio API's built-in oscillator for demo)
        // In production, these would be actual audio files
        this.registerSound('click', this.createClickSound());
        this.registerSound('success', this.createSuccessSound());
        this.registerSound('achievement', this.createAchievementSound());
        this.registerSound('place', this.createPlaceSound());
        this.registerSound('chapter', this.createChapterSound());
    }

    private registerSound(name: string, sound: Howl): void {
        this.sounds.set(name, sound);
    }

    public playBGM(): void {
        // Check audio context state
        if (Howler.ctx && Howler.ctx.state === 'suspended') {
            console.warn('⚠️ AudioContext is suspended. Waiting for interaction...');
        }

        this.bgm = new Howl({
            src: ['/assets/audio/bgm.mp3'],
            html5: false, // Switch to Web Audio API to avoid pool exhaustion
            loop: true,
            volume: 0,
            onload: () => {
                console.log('✅ BGM Loaded successfully. State:', Howler.ctx.state);
                if (!this.isBgmMuted && !this.isMuted) {
                    this.playBGMInternal();
                } else {
                    console.log('cBgm loaded but muted');
                }
            },
            onloaderror: (_id, err) => {
                console.error('❌ BGM failed to load:', err);
            },
            onplayerror: (_id, err) => {
                console.error('❌ BGM play error:', err);
                this.bgm!.once('unlock', () => {
                    console.log('🔓 Audio unlocked, playing...');
                    this.bgm!.play();
                });
            }
        });
    }

    private playBGMInternal(): void {
        if (!this.bgm) return;

        console.log('▶️ Starting playback...');
        this.bgmId = this.bgm.play();

        // Force volume ramp
        this.bgm.fade(0, 0.4, 2000, this.bgmId!);

        // Double check playing status after a delay
        setTimeout(() => {
            if (this.bgm && this.bgm.playing(this.bgmId!)) {
                console.log('🎵 BGM confirmed playing. Vol:', this.bgm.volume());
            } else {
                console.warn('⚠️ BGM not playing 1s after start command');
            }
        }, 1000);
    }

    public toggleMusic(): boolean {
        this.isBgmMuted = !this.isBgmMuted;

        if (this.bgm) {
            if (this.isBgmMuted) {
                this.bgm.mute(true);
            } else if (!this.isMuted) {
                // Only unmute if global mute is also off
                this.bgm.mute(false);
                if (!this.bgm.playing()) {
                    this.bgmId = this.bgm.play();
                    this.bgm.fade(0, 0.3, 1000, this.bgmId!);
                }
            }
        } else if (!this.isBgmMuted) {
            // Try to play if it wasn't loaded yet
            this.playBGM();
        }

        return this.isBgmMuted;
    }

    public isMusicMuted(): boolean {
        return this.isBgmMuted;
    }

    // ... (rest of sound creation methods) ...

    private createClickSound(): Howl {
        // Simple click - we'll use a sine wave burst
        return new Howl({
            src: ['data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YU'],
            volume: this.sfxVolume * 0.5,
            html5: false, // Use Web Audio API for short SFX
            onload: () => {
                console.log('Click sound ready');
            },
        });
    }

    private createSuccessSound(): Howl {
        return new Howl({
            src: ['data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YU'],
            volume: this.sfxVolume,
            html5: false,
        });
    }

    private createAchievementSound(): Howl {
        return new Howl({
            src: ['data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YU'],
            volume: this.sfxVolume * 0.7,
            html5: false,
        });
    }

    private createPlaceSound(): Howl {
        return new Howl({
            src: ['data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YU'],
            volume: this.sfxVolume * 0.6,
            html5: false,
        });
    }

    private createChapterSound(): Howl {
        return new Howl({
            src: ['data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YU'],
            volume: this.sfxVolume * 0.8,
            html5: false,
        });
    }

    play(soundName: string): void {
        if (this.isMuted) return;

        const sound = this.sounds.get(soundName);
        if (sound) {
            sound.play();
        } else {
            // Fallback to Web Audio API for immediate feedback
            this.playTone(soundName);
        }
    }

    // ... (playTone and switch case) ...

    // Generate tones using Web Audio API for immediate audio feedback
    private playTone(type: string): void {
        try {
            const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);

            // Different patterns for different sounds
            switch (type) {
                case 'click':
                    oscillator.frequency.value = 800;
                    oscillator.type = 'sine';
                    gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
                    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
                    oscillator.start();
                    oscillator.stop(audioContext.currentTime + 0.1);
                    break;

                case 'success':
                    oscillator.frequency.value = 523; // C5
                    oscillator.type = 'sine';
                    gainNode.gain.setValueAtTime(0.15, audioContext.currentTime);
                    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
                    oscillator.start();
                    // Add second note
                    setTimeout(() => {
                        const osc2 = audioContext.createOscillator();
                        const gain2 = audioContext.createGain();
                        osc2.connect(gain2);
                        gain2.connect(audioContext.destination);
                        osc2.frequency.value = 659; // E5
                        osc2.type = 'sine';
                        gain2.gain.setValueAtTime(0.15, audioContext.currentTime);
                        gain2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
                        osc2.start();
                        osc2.stop(audioContext.currentTime + 0.3);
                    }, 100);
                    oscillator.stop(audioContext.currentTime + 0.3);
                    break;

                case 'place':
                    oscillator.frequency.value = 440;
                    oscillator.type = 'triangle';
                    gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
                    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
                    oscillator.start();
                    oscillator.stop(audioContext.currentTime + 0.2);
                    break;

                case 'achievement':
                    // Fanfare-like sound
                    const notes = [523, 659, 784, 1047]; // C5, E5, G5, C6
                    notes.forEach((freq, i) => {
                        setTimeout(() => {
                            const osc = audioContext.createOscillator();
                            const gain = audioContext.createGain();
                            osc.connect(gain);
                            gain.connect(audioContext.destination);
                            osc.frequency.value = freq;
                            osc.type = 'sine';
                            gain.gain.setValueAtTime(0.12, audioContext.currentTime);
                            gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4);
                            osc.start();
                            osc.stop(audioContext.currentTime + 0.4);
                        }, i * 100);
                    });
                    break;

                case 'chapter':
                    // Celebratory chord
                    [523, 659, 784].forEach((freq) => {
                        const osc = audioContext.createOscillator();
                        const gain = audioContext.createGain();
                        osc.connect(gain);
                        gain.connect(audioContext.destination);
                        osc.frequency.value = freq;
                        osc.type = 'sine';
                        gain.gain.setValueAtTime(0.1, audioContext.currentTime);
                        gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.6);
                        osc.start();
                        osc.stop(audioContext.currentTime + 0.6);
                    });
                    break;
            }
        } catch (e) {
            // Audio not available, silently fail
            console.warn('Audio not available:', e);
        }
    }

    setSfxVolume(volume: number): void {
        this.sfxVolume = Math.max(0, Math.min(1, volume));
        this.sounds.forEach(sound => sound.volume(this.sfxVolume));
    }

    mute(): void {
        this.isMuted = true;
        Howler.mute(true);
    }

    unmute(): void {
        this.isMuted = false;
        Howler.mute(false);
        // If BGM was supposed to be playing but was muted globally, restore it (if not locally muted)
        if (this.bgm && !this.isBgmMuted && !this.bgm.playing()) {
            this.bgmId = this.bgm.play();
            this.bgm.fade(0, 0.3, 1000, this.bgmId!);
        }
    }

    toggleMute(): boolean {
        if (this.isMuted) {
            this.unmute();
        } else {
            this.mute();
        }
        return this.isMuted;
    }
}
